import React, { useMemo, useContext } from 'react'
import PropTypes from 'prop-types'
import styled, { keyframes, css } from 'styled-components'
import LazyLoad from 'react-lazyload'

import LinesEllipsisLoose from 'react-lines-ellipsis/lib/loose'

import spacing from 'styles/spacing'
import media from 'styles/media'

import ProductLabelInventory from 'components/ProductLabelInventory'
import ProductLabel from 'components/ProductLabel'

import { Icon, Fonts } from 'components/base-ui'
import { isShowLabelExcludedFromProtmoion } from 'helpers/product'
import BadgePromotionProduct from 'components/BadgePromotionProduct'

import { ProductMemberTierRankConditionsEnum, MemberProductIneligibleReasonEnum } from 'enums/product'
import { AppContext } from 'contexts/AppProvider'
import { displayPrice } from 'helpers'
import { BrandTypeEnum, THEMES_UI } from 'brands'
import { checkDineInIsOpen } from 'helpers/url'
import { getResizableURL } from 'helpers/image'
import { useTranslation } from 'react-i18next'

const { screenMdMin } = media

const ProductCard = ({
  mode = 'normal',
  addToCart = false,
  addToCartIconName = 'plus-light',
  onAddToCart = (product) => {},
  data,
  vendor,
  lazyLoadScrollContainerID = '',
  overflow = true,
  membershipProgram,
  onClickProduct,
  isOutline = false,
  hideZeroPrice = false,
  nameCustomClass = '',
  addToCartIconClass = '',
  ...rest
}) => {
  const {
    setIsOpenModalOnlyMemberMenu,
    setIsOpenModalOnlyMemberMenuSpecificCondition,
    setMembershipProgram,
    setArrayOnlyMemberMenuSpecificConditions,
    setMemberMenuIneligibleReason,
    brandType,
    themeUI,
    setVendorImageResizableURLForModalMemberMenu,
    cart,
    isThaiLang,
  } = useContext(AppContext)

  const { t } = useTranslation()

  const productName = isThaiLang ? data.nameTh : data.nameEn

  const getProductImage = (width, height) => {
    const fitContainCondition =
      brandType === BrandTypeEnum.THOUSAND_PRODUCTS ||
      brandType === BrandTypeEnum.MALL ||
      brandType === BrandTypeEnum.MALL_WITHOUT_GROCERY

    return getResizableURL(data.images, {
      width,
      height,
      formatType: fitContainCondition ? '_fmt-jpg_fit-contain' : '_fmt-jpg',
    })
  }

  const productImage1x = getProductImage('250', '250')
  const productImage2x = getProductImage('330', '330')
  const productImage3x = getProductImage('375', '375')

  const { isExclusive, isRecommended, tags, tagStr, isEligibleMember, isEligibleBirthMonth } = useMemo(() => {
    // Detect is Exclusive Tags
    let isExclusive = false
    let isRecommended = false
    const tags = []
    let tagStr = ''
    let isEligibleMember = data.memberTierRankConditions?.length > 0 ? true : false
    let isEligibleBirthMonth =
      isEligibleMember &&
      data.memberTierRankConditions.includes(ProductMemberTierRankConditionsEnum.BIRTHMONTH) &&
      !data.memberTierRankConditions.includes(ProductMemberTierRankConditionsEnum.ELIGIBLE)
        ? true
        : false

    data.tags?.forEach((tag) => {
      if (tag === 'Exclusive') {
        isExclusive = true
      }

      if (tag === 'Recommended') {
        isRecommended = true
      }

      // if Vegetarian or Vegan
      if (tag === 'Vegetarian' || tag === 'Vegan' || tag === 'Keto' || tag === 'Paleo') {
        tags.push(tag)
      }
    })

    tags.forEach((tag, i) => {
      if (i > 0) {
        tagStr += `, ${tag}`
      } else {
        tagStr = tag
      }
    })

    return {
      isExclusive,
      isRecommended,
      tags,
      tagStr,
      isEligibleMember,
      isEligibleBirthMonth,
    }
  }, [data?.id])

  const isActiveInventory = data?.locationProducts ? data.locationProducts[0]?.inStockQuantity ?? false : false
  const productStock = isActiveInventory !== false ? data.locationProducts[0].inStockQuantity : 0
  const isOutOfStock = isActiveInventory !== false && data.locationProducts[0].inStockQuantity === 0 ? true : false
  const ismMemberItem = isEligibleMember || isEligibleBirthMonth ? true : false

  let labelItem = null
  if (
    isEligibleBirthMonth ||
    (isEligibleMember && data.memberIneligibleReason === MemberProductIneligibleReasonEnum.TIER_BIRTHMONTH_REQUIRED)
  ) {
    labelItem = (
      <ProductLabelWrapper>
        <ProductLabel primary>
          <Icon name="birthday-cake" />{' '}
          <Fonts.SmallTitleBold as="span" className="product-label-text">
            {themeUI === THEMES_UI.GON ? t('tagBirthMonthSet') : t('tagBirthMonthMenu')}
          </Fonts.SmallTitleBold>
        </ProductLabel>
      </ProductLabelWrapper>
    )
  } else if (isEligibleMember) {
    labelItem = (
      <ProductLabelWrapper>
        <ProductLabel primary>
          <Icon name="star" />{' '}
          <Fonts.SmallTitleBold as="span" className="product-label-text">
            {themeUI === THEMES_UI.GON ? t('tagOnlyMember') : t('tagGenericOnlyMember')}
          </Fonts.SmallTitleBold>
        </ProductLabel>
      </ProductLabelWrapper>
    )
  } else if (isExclusive) {
    labelItem = (
      <ProductLabelWrapper>
        <ProductLabel $exclusive={true}>
          <Icon name="star" />{' '}
          <Fonts.SmallTitleBold as="span" className="product-label-text">
            {t('tagExclusive')}
          </Fonts.SmallTitleBold>
        </ProductLabel>
      </ProductLabelWrapper>
    )
  } else if (isRecommended) {
    labelItem = (
      <ProductLabelWrapper recommended>
        <ProductLabel tertiary>
          <Icon name="thumbsup-solid" />{' '}
          <Fonts.SmallTitleBold as="span" className="product-label-text">
            {t('tagRecommended')}
          </Fonts.SmallTitleBold>
        </ProductLabel>
      </ProductLabelWrapper>
    )
  }

  const labelExcludedPromotion =
    (checkDineInIsOpen() && data?.excludedFromPromotion) ||
    isShowLabelExcludedFromProtmoion({ product: data, cart }) ? (
      <LabelExcludedPromotion>{t('productExcludedPromotionShort')}</LabelExcludedPromotion>
    ) : null

  const showCardPrice = !(data.totalPriceSatangs === 0 && hideZeroPrice)

  const CardContent = (
    <>
      <CardThumbnailWrapper
        onClick={() => onClickProduct(data)}
        mode={mode}
        $exclusive={isExclusive}
        $recommended={isRecommended}
        $memberItem={ismMemberItem}
      >
        {lazyLoadScrollContainerID ? (
          <LazyLoad
            scrollContainer={`#${lazyLoadScrollContainerID}`}
            overflow={overflow}
            once
            offset={200}
            placeholder={
              <PlaceholderImage
                style={{
                  backgroundImage: `url('${process.env.PUBLIC_URL}/assets/placeholder-product-img.png')`,
                }}
              />
            }
          >
            <CardThumbnail
              $exclusive={isExclusive}
              $recommended={isRecommended}
              image1x={productImage1x}
              image2x={productImage2x}
              image3x={productImage3x}
            />
          </LazyLoad>
        ) : (
          <CardThumbnail
            $exclusive={isExclusive}
            $recommended={isRecommended}
            image1x={productImage1x}
            image2x={productImage2x}
            image3x={productImage3x}
          />
        )}

        {labelItem}

        {isActiveInventory !== false && (
          <ProductLabelInventoryWrapper
            themeUI={THEMES_UI.GON}
            isEligibleMember={isEligibleMember && !isEligibleBirthMonth}
          >
            <ProductLabelInventory number={productStock} />
          </ProductLabelInventoryWrapper>
        )}

        {data?.promotionBadge && (
          <BadgePromotionWrapper>
            <BadgePromotionProduct promotionStrategyType={data.promotionBadge} />
          </BadgePromotionWrapper>
        )}
      </CardThumbnailWrapper>

      {addToCart ? (
        <>
          <CardPriceSection>
            <div>
              <CardName onClick={() => onClickProduct(data)}>
                <LinesEllipsisLoose className={nameCustomClass} text={productName} maxLine="2" lineHeight="20" />
              </CardName>
              {tags.length > 0 && <CardTags>{tagStr}</CardTags>}
              {showCardPrice && (
                <CardPrice onClick={() => onClickProduct(data)}>
                  {data.discountSatangs > 0 ? (
                    <CardPriceBeforeDiscount>฿{displayPrice(data.priceSatangs / 100)}</CardPriceBeforeDiscount>
                  ) : null}
                  ฿{displayPrice(data.totalPriceSatangs / 100)}
                </CardPrice>
              )}
            </div>

            <BtnAddToCartPlus
              className="button"
              data-testid="add-to-cart-btn"
              hide={isOutOfStock}
              isOutline={isOutline}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                e.currentTarget.classList.remove('touch')
                if (data.memberIneligibleReason === MemberProductIneligibleReasonEnum.MEMBERSHIP_REQUIRED) {
                  setVendorImageResizableURLForModalMemberMenu(vendor?.logoImage?.resizableURL)
                  setIsOpenModalOnlyMemberMenu(true)
                } else if (
                  data.memberIneligibleReason === MemberProductIneligibleReasonEnum.TIER_BIRTHMONTH_REQUIRED ||
                  data.memberIneligibleReason === MemberProductIneligibleReasonEnum.TIER_INELIGIBLE
                ) {
                  setVendorImageResizableURLForModalMemberMenu(vendor?.logoImage?.resizableURL)
                  setArrayOnlyMemberMenuSpecificConditions(data.memberTierRankConditions)
                  setMemberMenuIneligibleReason(data.memberIneligibleReason)
                  setIsOpenModalOnlyMemberMenuSpecificCondition(true)
                  setMembershipProgram(membershipProgram)
                } else {
                  onAddToCart(data)
                }
              }}
            >
              <Icon className={addToCartIconClass} name={addToCartIconName} />
            </BtnAddToCartPlus>
          </CardPriceSection>
          {labelExcludedPromotion}
        </>
      ) : (
        <>
          <CardPriceSection>
            <CardName onClick={() => onClickProduct(data)}>
              <LinesEllipsisLoose className={nameCustomClass} text={productName} maxLine="2" lineHeight="20" />
            </CardName>
            {tags.length > 0 && <CardTags>{tagStr}</CardTags>}
            {showCardPrice && (
              <CardPrice margin={tags.length > 0 ? true : false} onClick={() => onClickProduct(data)}>
                {data.discountSatangs > 0 ? (
                  <CardPriceBeforeDiscount>฿{displayPrice(data.priceSatangs / 100)}</CardPriceBeforeDiscount>
                ) : null}
                ฿{displayPrice(data.totalPriceSatangs / 100)}
              </CardPrice>
            )}
          </CardPriceSection>
          {labelExcludedPromotion}
        </>
      )}
    </>
  )

  return (
    <Card mode={mode} {...rest} data-testid={productName}>
      {CardContent}
    </Card>
  )
}

ProductCard.propTypes = {
  mode: PropTypes.oneOf(['normal', 'slide']),
  thumbnailWidth: PropTypes.string,
  thumbnailHeight: PropTypes.string,
  addToCart: PropTypes.bool,
  onAddToCart: PropTypes.func,
  vendor: PropTypes.shape({
    logoImage: PropTypes.shape({
      resizableURL: PropTypes.string,
    }),
    nameTh: PropTypes.string,
    nameEn: PropTypes.string,
  }),
  location: PropTypes.shape({
    id: PropTypes.number,
  }),
  lazyLoadScrollContainerID: PropTypes.string,
  hideZeroPrice: PropTypes.bool,
}

export default ProductCard

const FadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`

const Card = styled.div`
  margin-bottom: 5px;
  width: 50%;
  padding-top: 15px;
  padding-left: 10px;
  padding-right: 10px;
  scroll-snap-align: start;

  @media (${screenMdMin}) {
    scroll-snap-align: inherit;
  }

  ${(props) =>
    props.mode === 'slide'
      ? `
    margin-right: 20px;
    margin-bottom: 0;
    display: inline-block;
    width: 135px;
    padding-left: 0;
    padding-right: 0;
    padding-bottom: 20px;
    vertical-align: top;

    &:first-child {
      margin-left: 20px;
    }
  `
      : ''}
`

const CardThumbnailWrapper = styled.div`
  position: relative;
  margin-bottom: ${spacing.md}px;
  width: 100%;
  border-radius: 10px;

  ${(props) =>
    props.$recommended
      ? `
    background: ${props.theme.colors.tertiary};
    `
      : ''}

  ${(props) =>
    props.$exclusive
      ? `
    background: ${props.theme.colors.success};
    `
      : ''}
  
  ${(props) =>
    props.$memberItem
      ? `
    background: ${props.theme.colors.primary};
    `
      : ''}

  &::before {
    content: '';
    display: block;
    padding-top: 100%;
  }

  ${(props) =>
    props.mode === 'slide'
      ? `
    height: 180px;

    &::before {
      display: none;
    }
  `
      : ''}
`

const CardThumbnail = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 10px;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  animation: ${FadeIn} 0.6s forwards;

  background-image: -webkit-image-set(
    url(${({ image1x }) => `'${image1x}'`}) 1x,
    url(${({ image2x }) => `'${image2x}'`}) 2x,
    url(${({ image3x }) => `'${image3x}'`}) 3x
  );
  background-image: image-set(
    url(${({ image1x }) => `'${image1x}'`}) 1x,
    url(${({ image2x }) => `'${image2x}'`}) 2x,
    url(${({ image3x }) => `'${image3x}'`}) 3x
  );

  ${({ $exclusive, $recommended }) =>
    ($exclusive || $recommended) &&
    css`
      border-radius: 8px;
      top: 3px;
      right: 3px;
      bottom: 3px;
      left: 3px;
    `}
`

const CardName = styled(Fonts.BaseTitleBold)`
  color: ${(props) => props.theme.colors.base};
  white-space: initial;
  padding-right: ${spacing.sm}px;
  margin-bottom: ${spacing.xs}px;
  font-size: 13px;
  line-height: 20px;

  ${(props) =>
    props.isPadding
      ? `
    padding-right: 40px;
    `
      : ''}
`

const CardPriceSection = styled.div`
  display: flex;
  justify-content: space-between;
`

const CardTags = styled.div`
  margin-bottom: 2px;
  font-size: 10px;
  color: ${(props) => props.theme.colors.gray300};
  text-overflow: ellipsis;
  overflow: hidden;
`

const CardPriceBeforeDiscount = styled.span`
  margin-right: 5px;
  color: ${(props) => props.theme.colors.baseGrey};
  text-decoration: line-through;
`

const CardPrice = styled(Fonts.BaseTitleBold)`
  color: ${(props) => props.theme.colors.volcano500};

  ${(props) =>
    props.margin
      ? `
    margin-left: 5px;
    `
      : ''}
`

const BtnAddToCartPlus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  min-width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.1), 0 6px 20px rgba(0, 0, 0, 0.05);
  transition: 0.6s;

  i {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.white};
    transition: 0.6s;
  }

  &:active {
    transition: 0s;
    background-color: ${({ theme }) => theme.colors.white};
    border: none;

    i {
      transition: 0s;
      color: ${({ theme }) => theme.colors.primary};
    }
  }

  ${({ isOutline, theme }) =>
    isOutline &&
    `
    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.primary};

    i {
      color: ${theme.colors.primary};
    }

    &:active {
      background-color: ${theme.colors.primary};
      i {
        color: ${theme.colors.white}
      }
    }
  `}

  ${(props) =>
    props.hide
      ? `
    visibility: hidden;
    `
      : ''}
`

const PlaceholderImage = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-size: cover;
  background-position: center;
`

const ProductLabelInventoryWrapper = styled.div`
  position: absolute;
  left: 10px;
  bottom: ${({ themeUI, isEligibleMember }) => (themeUI === THEMES_UI.GON && isEligibleMember ? '40px' : '13px')};
`

const ProductLabelWrapper = styled.div`
  position: absolute;
  bottom: 13px;
  right: -4px;
`

const LabelExcludedPromotion = styled(Fonts.SmallTitleRegular)`
  margin-top: 5px;
  font-size: 10px;
  color: ${(props) => props.theme.colors.infoDark};
`

const BadgePromotionWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
`
